import dagre from 'dagre';
import { Node, Edge, Position, MarkerType } from 'reactflow';

export type LayoutDirection = 'TB' | 'LR' | 'BT' | 'RL';

interface LayoutOptions {
  direction?: LayoutDirection;
  nodeWidth?: number;
  nodeHeight?: number;
  nodesep?: number;
  edgesep?: number;
  ranksep?: number;
  marginx?: number;
  marginy?: number;
}

interface SnakeLayoutOptions {
  maxRanksPerRow?: number;
  nodeWidth?: number;
  nodeHeight?: number;
  gapX?: number;
  gapY?: number;
  rowGap?: number;
}

/**
 * Snake/serpentine layout: uses dagre for rank assignment, then folds ranks
 * into alternating LR/RL rows to minimize total canvas area.
 *
 * Row 0: rank0 → rank1 → rank2 → rank3  (left to right)
 *                                    ↓
 * Row 1: rank7 ← rank6 ← rank5 ← rank4  (right to left)
 *    ↓
 * Row 2: rank8 → rank9 → ...             (left to right)
 */
export const getSnakeLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  options: SnakeLayoutOptions = {}
): { nodes: Node[]; edges: Edge[] } => {
  const {
    maxRanksPerRow = 4,
    nodeWidth = 288,
    nodeHeight = 128,
    gapX = 60,
    gapY = 40,
    rowGap = 80,
  } = options;

  if (nodes.length === 0) return { nodes, edges };

  // Step 1: Run dagre LR to get rank assignments (handles branching correctly)
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'LR', nodesep: 40, edgesep: 20, ranksep: 80 });

  nodes.forEach((node) => {
    const dims = getNodeDimensions(node.type);
    dagreGraph.setNode(node.id, { width: dims.width, height: dims.height });
  });
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });
  dagre.layout(dagreGraph);

  // Step 2: Extract dagre positions and cluster nodes into rank columns by x-center
  const dagrePositions = new Map<string, { x: number; y: number }>();
  nodes.forEach((n) => {
    const pos = dagreGraph.node(n.id);
    dagrePositions.set(n.id, { x: pos.x, y: pos.y });
  });

  // Cluster by x-center with tolerance (nodes in same dagre rank share same x)
  const xValues = Array.from(dagrePositions.values()).map((p) => p.x);
  const uniqueXs = Array.from(new Set(xValues)).sort((a, b) => a - b);

  // Merge x-values that are within tolerance into the same rank
  const RANK_TOLERANCE = 10;
  const rankCenters: number[] = [];
  for (const x of uniqueXs) {
    if (rankCenters.length === 0 || x - rankCenters[rankCenters.length - 1] > RANK_TOLERANCE) {
      rankCenters.push(x);
    }
  }

  // Assign each node to a rank index
  const nodeRankMap = new Map<string, number>();
  const rankNodeMap = new Map<number, string[]>(); // rankIndex → nodeIds

  nodes.forEach((n) => {
    const pos = dagrePositions.get(n.id)!;
    let bestRank = 0;
    let bestDist = Infinity;
    for (let i = 0; i < rankCenters.length; i++) {
      const dist = Math.abs(pos.x - rankCenters[i]);
      if (dist < bestDist) {
        bestDist = dist;
        bestRank = i;
      }
    }
    nodeRankMap.set(n.id, bestRank);
    if (!rankNodeMap.has(bestRank)) rankNodeMap.set(bestRank, []);
    rankNodeMap.get(bestRank)!.push(n.id);
  });

  // Sort nodes within each rank by dagre y-position (preserves branch ordering)
  rankNodeMap.forEach((nodeIds) => {
    nodeIds.sort((a, b) => dagrePositions.get(a)!.y - dagrePositions.get(b)!.y);
  });

  // Step 3: Fold ranks into snake rows
  const totalRanks = rankCenters.length;
  const rows: number[][] = []; // each row is an array of rank indices in display order

  for (let i = 0; i < totalRanks; i += maxRanksPerRow) {
    const rowIndex = rows.length;
    const ranksInRow: number[] = [];
    for (let j = i; j < Math.min(i + maxRanksPerRow, totalRanks); j++) {
      ranksInRow.push(j);
    }
    // Reverse even/odd rows for snake pattern
    if (rowIndex % 2 === 1) {
      ranksInRow.reverse();
    }
    rows.push(ranksInRow);
  }

  // Step 4: Calculate row heights (max nodes in any rank within that row)
  const rowHeights: number[] = rows.map((ranksInRow) => {
    let maxNodesInRank = 1;
    for (const rankIdx of ranksInRow) {
      const nodeIds = rankNodeMap.get(rankIdx) || [];
      maxNodesInRank = Math.max(maxNodesInRank, nodeIds.length);
    }
    return maxNodesInRank * (nodeHeight + gapY) - gapY;
  });

  // Step 5: Position nodes
  const nodePositionMap = new Map<string, { x: number; y: number }>();
  let currentY = 0;

  rows.forEach((ranksInRow, rowIndex) => {
    const rowHeight = rowHeights[rowIndex];

    ranksInRow.forEach((rankIdx, colIndex) => {
      const nodeIds = rankNodeMap.get(rankIdx) || [];
      const x = colIndex * (nodeWidth + gapX);

      // Center nodes vertically within the row
      const totalNodesHeight = nodeIds.length * (nodeHeight + gapY) - gapY;
      const yOffset = (rowHeight - totalNodesHeight) / 2;

      nodeIds.forEach((nodeId, nodeIndex) => {
        const node = nodes.find((n) => n.id === nodeId);
        const dims = getNodeDimensions(node?.type);

        nodePositionMap.set(nodeId, {
          x: x + (nodeWidth - dims.width) / 2,
          y: currentY + yOffset + nodeIndex * (nodeHeight + gapY) + (nodeHeight - dims.height) / 2,
        });
      });
    });

    currentY += rowHeight + rowGap;
  });

  // Build a lookup: nodeId → rowIndex for edge routing
  const nodeRowMap = new Map<string, number>();
  rows.forEach((ranksInRow, rowIndex) => {
    for (const rankIdx of ranksInRow) {
      const nodeIds = rankNodeMap.get(rankIdx) || [];
      nodeIds.forEach((id) => nodeRowMap.set(id, rowIndex));
    }
  });

  // Step 6: Apply positions and set handle directions
  const layoutedNodes = nodes.map((node) => {
    const pos = nodePositionMap.get(node.id) || { x: 0, y: 0 };
    const row = nodeRowMap.get(node.id) ?? 0;
    const isReversedRow = row % 2 === 1;

    return {
      ...node,
      position: snapToGrid(pos),
      sourcePosition: isReversedRow ? Position.Left : Position.Right,
      targetPosition: isReversedRow ? Position.Right : Position.Left,
    };
  });

  // Step 7: Update edge types for snake routing with node-crossing avoidance
  // Build NodeLike array from layouted nodes for crossing detection
  const nodeLikes: NodeLike[] = layoutedNodes.map((n) => ({
    id: n.id,
    position: n.position,
    type: n.type,
  }));

  const layoutedEdges = edges.map((edge) => {
    const sourceRow = nodeRowMap.get(edge.source) ?? 0;
    const targetRow = nodeRowMap.get(edge.target) ?? 0;

    const sourceNodeLike = nodeLikes.find((n) => n.id === edge.source);
    const targetNodeLike = nodeLikes.find((n) => n.id === edge.target);

    if (sourceRow !== targetRow) {
      // Cross-row edge: use selectEdgeHandles for smart routing with crossing avoidance
      let sourceHandle = 'bottom';
      let targetHandle = 'top';

      if (sourceNodeLike && targetNodeLike) {
        const handles = selectEdgeHandles(sourceNodeLike, targetNodeLike, nodeLikes);
        sourceHandle = handles.sourceHandle;
        targetHandle = handles.targetHandle;
      }

      return {
        ...edge,
        type: 'smoothstep',
        sourceHandle,
        targetHandle,
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#3b82f6' },
      };
    }

    // Same-row edge: prefer row-direction handles, but fall back if they cross a node
    const isReversed = sourceRow % 2 === 1;
    let sourceHandle = isReversed ? 'left' : 'right';
    let targetHandle = isReversed ? 'right' : 'left';

    if (sourceNodeLike && targetNodeLike) {
      // Check if row-direction handles would cross an intermediate node
      if (wouldEdgeCrossNode(sourceNodeLike, targetNodeLike, sourceHandle, targetHandle, nodeLikes)) {
        // Fall back to smart handle selection
        const handles = selectEdgeHandles(sourceNodeLike, targetNodeLike, nodeLikes);
        sourceHandle = handles.sourceHandle;
        targetHandle = handles.targetHandle;
      }
    }

    return {
      ...edge,
      type: 'smoothstep',
      sourceHandle,
      targetHandle,
      markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#3b82f6' },
    };
  });

  return { nodes: layoutedNodes, edges: layoutedEdges };
};

/**
 * Snake layout with edge routing post-processing
 */
export const getSnakeLayoutedElementsWithRouting = async (
  nodes: Node[],
  edges: Edge[],
  options: SnakeLayoutOptions = {}
): Promise<{ nodes: Node[]; edges: Edge[] }> => {
  const layouted = getSnakeLayoutedElements(nodes, edges, options);
  ensureProperEdgeRouting();
  return layouted;
};

/**
 * Get layouted elements using dagre for automatic graph layout
 */
export const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): { nodes: Node[]; edges: Edge[] } => {
  const {
    direction = 'TB',
    nodeWidth = 288, // Updated to match standardized dimensions
    nodeHeight = 128, // Updated to match standardized dimensions
    nodesep = 100,
    edgesep = 50,
    ranksep = 150,
    marginx = 50,
    marginy = 50,
  } = options;

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Set graph configuration
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep,
    edgesep,
    ranksep,
    marginx,
    marginy,
  });

  // Add nodes to the graph using per-type dimensions
  nodes.forEach((node) => {
    const dims = getNodeDimensions(node.type);
    dagreGraph.setNode(node.id, { width: dims.width, height: dims.height });
  });

  // Add edges to the graph
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate the layout
  dagre.layout(dagreGraph);

  // Apply the calculated positions to nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const dims = getNodeDimensions(node.type);

    return {
      ...node,
      targetPosition: getTargetPosition(direction),
      sourcePosition: getSourcePosition(direction),
      position: {
        x: nodeWithPosition.x - dims.width / 2,
        y: nodeWithPosition.y - dims.height / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

/**
 * Ensure proper edge routing by forcing React Flow to recalculate paths
 * This resolves the issue where arrows are not straight after auto-layout
 */
export const ensureProperEdgeRouting = (): void => {
  // Force React Flow to recalculate edge paths by dispatching a resize event
  // This ensures arrows are properly aligned after layout changes
  setTimeout(() => {
    window.dispatchEvent(new Event('resize'));
  }, 100);
};

/**
 * Enhanced auto-layout that ensures proper edge routing
 * This function first applies Dagre layout and then ensures edges are properly routed
 */
export const getLayoutedElementsWithRouting = async (
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): Promise<{ nodes: Node[]; edges: Edge[] }> => {
  // First, apply the standard Dagre layout
  const layouted = getLayoutedElements(nodes, edges, options);
  
  // Then ensure proper edge routing
  ensureProperEdgeRouting();
  
  return layouted;
};

/**
 * Get source position based on layout direction
 */
const getSourcePosition = (direction: LayoutDirection): Position => {
  switch (direction) {
    case 'TB':
      return Position.Bottom;
    case 'BT':
      return Position.Top;
    case 'LR':
      return Position.Right;
    case 'RL':
      return Position.Left;
    default:
      return Position.Bottom;
  }
};

/**
 * Get target position based on layout direction
 */
const getTargetPosition = (direction: LayoutDirection): Position => {
  switch (direction) {
    case 'TB':
      return Position.Top;
    case 'BT':
      return Position.Bottom;
    case 'LR':
      return Position.Left;
    case 'RL':
      return Position.Right;
    default:
      return Position.Top;
  }
};

/**
 * Grid size for snapping
 */
export const GRID_SIZE = 20;

/**
 * Constants for vertical layout positioning (AC-705)
 */
const VERTICAL_SPACING = 120; // Gap between parent bottom and new node top
const VERTICAL_OFFSET_INCREMENT = 180; // When collision, try this far down
const HORIZONTAL_OFFSET_INCREMENT = 300; // When vertical fails, try this far right
const MAX_PLACEMENT_ATTEMPTS = 10;

/**
 * Snap position to grid
 */
export const snapToGrid = (position: { x: number; y: number }): { x: number; y: number } => {
  return {
    x: Math.round(position.x / GRID_SIZE) * GRID_SIZE,
    y: Math.round(position.y / GRID_SIZE) * GRID_SIZE,
  };
};

/**
 * Check if two nodes have collision
 */
export const hasCollision = (
  position: { x: number; y: number },
  existingNodes: Node[],
  newNodeType: string = 'default',
  excludeNodeId?: string
): boolean => {
  const newNodeDims = getNodeDimensions(newNodeType);
  
  return existingNodes.some((node) => {
    if (node.id === excludeNodeId) return false;
    
    const nodeDims = getNodeDimensions(node.type);
    
    return (
      position.x < node.position.x + nodeDims.width &&
      position.x + newNodeDims.width > node.position.x &&
      position.y < node.position.y + nodeDims.height &&
      position.y + newNodeDims.height > node.position.y
    );
  });
};

/**
 * Calculate optimal position for a new node
 */
export const calculateNewNodePosition = (
  parentNode: Node | undefined,
  existingNodes: Node[],
  nodeType: string
): { x: number; y: number } => {
  const spacing = { x: 300, y: 180 };
  
  // Default position if no parent
  if (!parentNode) {
    return snapToGrid({ x: 100, y: 100 });
  }

  // Get parent node dimensions
  const parentDims = getNodeDimensions(parentNode.type);
  
  // Start with position to the right of parent
  const basePosition = {
    x: parentNode.position.x + parentDims.width + spacing.x / 2,
    y: parentNode.position.y,
  };

  // Check for collisions and adjust
  let finalPosition = basePosition;
  let offsetY = 0;
  let attempts = 0;
  const maxAttempts = 10;

  while (
    hasCollision(finalPosition, existingNodes, nodeType) && 
    attempts < maxAttempts
  ) {
    offsetY += spacing.y;
    finalPosition = {
      x: basePosition.x,
      y: basePosition.y + offsetY,
    };
    attempts++;
    
    // If we've tried too many times, try a different x position
    if (attempts === 5) {
      basePosition.x += spacing.x;
      offsetY = 0;
    }
  }

  return snapToGrid(finalPosition);
};

/**
 * Calculate optimal position for a new node BELOW the parent (AC-705)
 *
 * This function places new nodes in a vertical (TB) layout with collision detection:
 * 1. Base position: centered below parent with VERTICAL_SPACING gap
 * 2. If collision detected, try positions further down (VERTICAL_OFFSET_INCREMENT)
 * 3. After multiple vertical attempts fail, offset horizontally
 * 4. Snap final position to grid
 */
export const calculateNewNodePositionBelow = (
  parentNode: Node | undefined,
  existingNodes: Node[],
  nodeType: string
): { x: number; y: number } => {
  // Default position if no parent
  if (!parentNode) {
    return snapToGrid({ x: 100, y: 100 });
  }

  // Get dimensions
  const parentDims = getNodeDimensions(parentNode.type);
  const newNodeDims = getNodeDimensions(nodeType);

  // Calculate base position: centered below parent
  const baseX = parentNode.position.x + (parentDims.width / 2) - (newNodeDims.width / 2);
  const baseY = parentNode.position.y + parentDims.height + VERTICAL_SPACING;

  let candidatePosition = { x: baseX, y: baseY };
  let horizontalOffset = 0;
  let verticalOffset = 0;
  let attempts = 0;
  const verticalAttemptsBeforeHorizontal = 5;

  while (
    hasCollision(candidatePosition, existingNodes, nodeType) &&
    attempts < MAX_PLACEMENT_ATTEMPTS
  ) {
    attempts++;

    // Try vertical offsets first
    if (attempts <= verticalAttemptsBeforeHorizontal) {
      verticalOffset += VERTICAL_OFFSET_INCREMENT;
      candidatePosition = {
        x: baseX + horizontalOffset,
        y: baseY + verticalOffset,
      };
    } else {
      // After vertical attempts exhausted, try horizontal offset
      horizontalOffset += HORIZONTAL_OFFSET_INCREMENT;
      verticalOffset = 0; // Reset vertical offset
      candidatePosition = {
        x: baseX + horizontalOffset,
        y: baseY + verticalOffset,
      };
    }
  }

  return snapToGrid(candidatePosition);
};

/**
 * Get node dimensions based on type
 */
export const getNodeDimensions = (nodeType: string | undefined): { width: number; height: number } => {
  switch (nodeType) {
    case 'add':
      return { width: 48, height: 48 };
    case 'end':
      return { width: 140, height: 70 };
    case 'start':
      return { width: 288, height: 128 }; // w-72 h-32
    case 'conditional':
      return { width: 288, height: 128 }; // w-72 h-32
    case 'userPrompt':
      return { width: 288, height: 128 }; // w-72 h-32
    case 'agent':
      return { width: 288, height: 128 }; // w-72 h-32
    default:
      return { width: 288, height: 128 }; // w-72 h-32 - use standard size as default
  }
};

/**
 * Minimal node interface for edge handle calculations.
 * Works with both React Flow Node objects and plain position objects.
 */
interface NodeLike {
  id: string;
  position: { x: number; y: number };
  type?: string;
}

/**
 * Get the absolute pixel position of a handle on a node.
 */
const getHandleAbsolutePosition = (
  node: NodeLike,
  handle: string,
  dims: { width: number; height: number }
): { x: number; y: number } => {
  switch (handle) {
    case 'top':
      return { x: node.position.x + dims.width / 2, y: node.position.y };
    case 'bottom':
      return { x: node.position.x + dims.width / 2, y: node.position.y + dims.height };
    case 'left':
      return { x: node.position.x, y: node.position.y + dims.height / 2 };
    case 'right':
      return { x: node.position.x + dims.width, y: node.position.y + dims.height / 2 };
    default:
      return { x: node.position.x + dims.width / 2, y: node.position.y + dims.height };
  }
};

/**
 * Check if a rectangular segment overlaps any intermediate node.
 */
const segmentCrossesNode = (
  minX: number, maxX: number, minY: number, maxY: number,
  allNodes: NodeLike[],
  skipIds: Set<string>
): boolean => {
  for (const node of allNodes) {
    if (skipIds.has(node.id)) continue;
    if (node.type === 'add') continue;

    const nodeDims = getNodeDimensions(node.type);
    const nodeLeft = node.position.x;
    const nodeRight = node.position.x + nodeDims.width;
    const nodeTop = node.position.y;
    const nodeBottom = node.position.y + nodeDims.height;

    if (nodeRight > minX && nodeLeft < maxX &&
        nodeBottom > minY && nodeTop < maxY) {
      return true;
    }
  }
  return false;
};

/**
 * Check if a smoothstep edge path between two handles would cross through any intermediate node.
 *
 * Instead of a single bounding-box corridor (which creates massive false positives when
 * source and target are far apart), this checks the 3 segments of a smoothstep path:
 * - Vertical handles (top/bottom → top/bottom): vertical → horizontal → vertical
 * - Horizontal handles (left/right → left/right): horizontal → vertical → horizontal
 * - Mixed handles: L-shaped 2-segment path
 *
 * This avoids false positives when edges route through empty gaps between rows.
 */
const wouldEdgeCrossNode = (
  sourceNode: NodeLike,
  targetNode: NodeLike,
  sourceHandle: string,
  targetHandle: string,
  allNodes: NodeLike[]
): boolean => {
  const sourceDims = getNodeDimensions(sourceNode.type);
  const targetDims = getNodeDimensions(targetNode.type);

  const sourcePos = getHandleAbsolutePosition(sourceNode, sourceHandle, sourceDims);
  const targetPos = getHandleAbsolutePosition(targetNode, targetHandle, targetDims);

  const padding = 10;
  const skipIds = new Set([sourceNode.id, targetNode.id]);

  const isVerticalSource = sourceHandle === 'top' || sourceHandle === 'bottom';
  const isVerticalTarget = targetHandle === 'top' || targetHandle === 'bottom';

  if (isVerticalSource && isVerticalTarget) {
    // Smoothstep path: vertical from source → horizontal at midY → vertical to target
    const midY = (sourcePos.y + targetPos.y) / 2;

    // Segment 1: vertical from source handle to midY
    if (segmentCrossesNode(
      sourcePos.x - padding, sourcePos.x + padding,
      Math.min(sourcePos.y, midY), Math.max(sourcePos.y, midY),
      allNodes, skipIds
    )) return true;

    // Segment 2: horizontal from sourceX to targetX at midY
    if (segmentCrossesNode(
      Math.min(sourcePos.x, targetPos.x) - padding, Math.max(sourcePos.x, targetPos.x) + padding,
      midY - padding, midY + padding,
      allNodes, skipIds
    )) return true;

    // Segment 3: vertical from midY to target handle
    if (segmentCrossesNode(
      targetPos.x - padding, targetPos.x + padding,
      Math.min(midY, targetPos.y), Math.max(midY, targetPos.y),
      allNodes, skipIds
    )) return true;

  } else if (!isVerticalSource && !isVerticalTarget) {
    // Smoothstep path: horizontal from source → vertical at midX → horizontal to target
    const midX = (sourcePos.x + targetPos.x) / 2;

    // Segment 1: horizontal from source handle to midX
    if (segmentCrossesNode(
      Math.min(sourcePos.x, midX), Math.max(sourcePos.x, midX),
      sourcePos.y - padding, sourcePos.y + padding,
      allNodes, skipIds
    )) return true;

    // Segment 2: vertical from sourceY to targetY at midX
    if (segmentCrossesNode(
      midX - padding, midX + padding,
      Math.min(sourcePos.y, targetPos.y) - padding, Math.max(sourcePos.y, targetPos.y) + padding,
      allNodes, skipIds
    )) return true;

    // Segment 3: horizontal from midX to target handle
    if (segmentCrossesNode(
      Math.min(midX, targetPos.x), Math.max(midX, targetPos.x),
      targetPos.y - padding, targetPos.y + padding,
      allNodes, skipIds
    )) return true;

  } else {
    // Mixed handles (e.g., bottom → left): L-shaped path
    const cornerX = isVerticalSource ? sourcePos.x : targetPos.x;
    const cornerY = isVerticalSource ? targetPos.y : sourcePos.y;

    // Segment 1: from source to corner
    if (segmentCrossesNode(
      Math.min(sourcePos.x, cornerX) - padding, Math.max(sourcePos.x, cornerX) + padding,
      Math.min(sourcePos.y, cornerY) - padding, Math.max(sourcePos.y, cornerY) + padding,
      allNodes, skipIds
    )) return true;

    // Segment 2: from corner to target
    if (segmentCrossesNode(
      Math.min(cornerX, targetPos.x) - padding, Math.max(cornerX, targetPos.x) + padding,
      Math.min(cornerY, targetPos.y) - padding, Math.max(cornerY, targetPos.y) + padding,
      allNodes, skipIds
    )) return true;
  }

  return false;
};

/**
 * Select optimal edge handles based on relative node positions.
 * Uses dominant-direction routing with node-crossing avoidance.
 *
 * Instead of priority-based conditions (which can flip-flop between handles),
 * this uses the dominant direction (larger of |dx| vs |dy|) to choose handles,
 * then checks if the path would cross intermediate nodes and swaps if needed.
 */
export const selectEdgeHandles = (
  sourceNode: NodeLike,
  targetNode: NodeLike,
  allNodes?: NodeLike[]
): { sourceHandle: string; targetHandle: string } => {
  const sourceDims = getNodeDimensions(sourceNode.type);
  const targetDims = getNodeDimensions(targetNode.type);

  const sourceCenterX = sourceNode.position.x + sourceDims.width / 2;
  const sourceCenterY = sourceNode.position.y + sourceDims.height / 2;
  const targetCenterX = targetNode.position.x + targetDims.width / 2;
  const targetCenterY = targetNode.position.y + targetDims.height / 2;

  const dx = targetCenterX - sourceCenterX;
  const dy = targetCenterY - sourceCenterY;

  // Determine primary (dominant direction) and alternative handle pairs
  let primarySource: string;
  let primaryTarget: string;
  let altSource: string;
  let altTarget: string;

  // AC-1465: when the source and target are in different rows (|dy| >= one
  // averaged node height) prefer vertical routing even if |dx| > |dy|. Two
  // distinct rows are visually a "wrap" and the eye reads top->bottom much
  // more naturally than left->right. Without this bias the auto-layout
  // wraps a downstream-row edge sideways out of the upper-row node and
  // back across multiple nodes -- ugly and confusing. The pure |dy| >= |dx|
  // dominant-axis test is kept as the fallback for same-row layouts where
  // the horizontal-vs-vertical distinction is genuinely ambiguous.
  const avgNodeHeight = (sourceDims.height + targetDims.height) / 2;
  const differentRows = Math.abs(dy) >= avgNodeHeight;
  const verticalDominant = differentRows || Math.abs(dy) >= Math.abs(dx);

  if (verticalDominant) {
    primarySource = dy >= 0 ? 'bottom' : 'top';
    primaryTarget = dy >= 0 ? 'top' : 'bottom';
    altSource = dx >= 0 ? 'right' : 'left';
    altTarget = dx >= 0 ? 'left' : 'right';
  } else {
    primarySource = dx >= 0 ? 'right' : 'left';
    primaryTarget = dx >= 0 ? 'left' : 'right';
    altSource = dy >= 0 ? 'bottom' : 'top';
    altTarget = dy >= 0 ? 'top' : 'bottom';
  }

  // Check if primary path would cross any intermediate node
  if (allNodes && allNodes.length > 2) {
    const primaryCrosses = wouldEdgeCrossNode(sourceNode, targetNode, primarySource, primaryTarget, allNodes);

    if (primaryCrosses) {
      // Try alternative handles
      if (!wouldEdgeCrossNode(sourceNode, targetNode, altSource, altTarget, allNodes)) {
        return { sourceHandle: altSource, targetHandle: altTarget };
      }

      // Both primary and alt cross - try mixed handle pairs (L-shaped routing)
      // These route along the perimeter of the layout instead of through the middle
      const mixedPairs: [string, string][] = [
        [primarySource, altTarget],  // e.g., bottom → left (L-shape)
        [altSource, primaryTarget],  // e.g., right → top (L-shape)
      ];

      for (const [ms, mt] of mixedPairs) {
        if (!wouldEdgeCrossNode(sourceNode, targetNode, ms, mt, allNodes)) {
          return { sourceHandle: ms, targetHandle: mt };
        }
      }
    }
  }

  return { sourceHandle: primarySource, targetHandle: primaryTarget };
};

/**
 * Determine optimal edge type based on node alignment
 * Returns 'straight' for vertically-aligned nodes, 'smoothstep' otherwise
 */
export const determineEdgeType = (
  sourceNode: Node | undefined,
  targetNode: Node | undefined,
  alignmentTolerance: number = 50
): 'straight' | 'smoothstep' => {
  if (!sourceNode || !targetNode) return 'smoothstep';

  const sourceDims = getNodeDimensions(sourceNode.type);
  const targetDims = getNodeDimensions(targetNode.type);

  const sourceCenterX = sourceNode.position.x + sourceDims.width / 2;
  const targetCenterX = targetNode.position.x + targetDims.width / 2;

  const horizontalOffset = Math.abs(sourceCenterX - targetCenterX);

  return horizontalOffset <= alignmentTolerance ? 'straight' : 'smoothstep';
};

/**
 * Align nodes horizontally or vertically
 */
export const alignNodes = (
  nodes: Node[],
  selectedNodeIds: string[],
  alignment: 'left' | 'right' | 'center' | 'top' | 'bottom' | 'middle'
): Node[] => {
  const selectedNodes = nodes.filter((n) => selectedNodeIds.includes(n.id));
  if (selectedNodes.length < 2) return nodes;

  // Calculate relative positions to preserve distribution spacing
  const isHorizontalAlignment = alignment === 'left' || alignment === 'right' || alignment === 'center';
  const isVerticalAlignment = alignment === 'top' || alignment === 'bottom' || alignment === 'middle';

  // Store relative positions in the perpendicular axis to preserve distribution
  let relativePositions: Map<string, number> = new Map();
  
  if (isHorizontalAlignment) {
    // For horizontal alignment, preserve relative Y positions (vertical distribution)
    const minY = Math.min(...selectedNodes.map((n) => n.position.y));
    selectedNodes.forEach((n) => {
      relativePositions.set(n.id, n.position.y - minY);
    });
  } else if (isVerticalAlignment) {
    // For vertical alignment, preserve relative X positions (horizontal distribution)
    const minX = Math.min(...selectedNodes.map((n) => n.position.x));
    selectedNodes.forEach((n) => {
      relativePositions.set(n.id, n.position.x - minX);
    });
  }

  let referenceValue: number;

  switch (alignment) {
    case 'left':
      referenceValue = Math.min(...selectedNodes.map((n) => n.position.x));
      break;
    case 'right':
      // For right alignment, find the rightmost edge
      referenceValue = Math.max(...selectedNodes.map((n) => {
        const dims = getNodeDimensions(n.type);
        return n.position.x + dims.width;
      }));
      break;
    case 'center':
      // Calculate true center based on node boundaries
      const xCenters = selectedNodes.map((n) => {
        const dims = getNodeDimensions(n.type);
        return n.position.x + dims.width / 2;
      });
      referenceValue = xCenters.reduce((a, b) => a + b, 0) / xCenters.length;
      break;
    case 'top':
      referenceValue = Math.min(...selectedNodes.map((n) => n.position.y));
      break;
    case 'bottom':
      // For bottom alignment, find the bottommost edge
      referenceValue = Math.max(...selectedNodes.map((n) => {
        const dims = getNodeDimensions(n.type);
        return n.position.y + dims.height;
      }));
      break;
    case 'middle':
      // Calculate true middle based on node boundaries
      const yCenters = selectedNodes.map((n) => {
        const dims = getNodeDimensions(n.type);
        return n.position.y + dims.height / 2;
      });
      referenceValue = yCenters.reduce((a, b) => a + b, 0) / yCenters.length;
      break;
    default:
      return nodes;
  }

  return nodes.map((node) => {
    if (!selectedNodeIds.includes(node.id)) return node;

    const newPosition = { ...node.position };
    const nodeDims = getNodeDimensions(node.type);
    const relativePos = relativePositions.get(node.id) || 0;

    switch (alignment) {
      case 'left':
        newPosition.x = referenceValue;
        // Preserve relative Y position (vertical distribution)
        if (relativePositions.has(node.id)) {
          const minY = Math.min(...selectedNodes.map((n) => n.position.y));
          newPosition.y = minY + relativePos;
        }
        break;
      case 'right':
        newPosition.x = referenceValue - nodeDims.width;
        // Preserve relative Y position (vertical distribution)
        if (relativePositions.has(node.id)) {
          const minY = Math.min(...selectedNodes.map((n) => n.position.y));
          newPosition.y = minY + relativePos;
        }
        break;
      case 'center':
        newPosition.x = referenceValue - nodeDims.width / 2;
        // Preserve relative Y position (vertical distribution)
        if (relativePositions.has(node.id)) {
          const minY = Math.min(...selectedNodes.map((n) => n.position.y));
          newPosition.y = minY + relativePos;
        }
        break;
      case 'top':
        newPosition.y = referenceValue;
        // Preserve relative X position (horizontal distribution)
        if (relativePositions.has(node.id)) {
          const minX = Math.min(...selectedNodes.map((n) => n.position.x));
          newPosition.x = minX + relativePos;
        }
        break;
      case 'bottom':
        newPosition.y = referenceValue - nodeDims.height;
        // Preserve relative X position (horizontal distribution)
        if (relativePositions.has(node.id)) {
          const minX = Math.min(...selectedNodes.map((n) => n.position.x));
          newPosition.x = minX + relativePos;
        }
        break;
      case 'middle':
        newPosition.y = referenceValue - nodeDims.height / 2;
        // Preserve relative X position (horizontal distribution)
        if (relativePositions.has(node.id)) {
          const minX = Math.min(...selectedNodes.map((n) => n.position.x));
          newPosition.x = minX + relativePos;
        }
        break;
    }

    return {
      ...node,
      position: snapToGrid(newPosition),
    };
  });
};

/**
 * Distribute nodes evenly
 */
export const distributeNodes = (
  nodes: Node[],
  selectedNodeIds: string[],
  direction: 'horizontal' | 'vertical'
): Node[] => {
  const selectedNodes = nodes
    .filter((n) => selectedNodeIds.includes(n.id))
    .sort((a, b) =>
      direction === 'horizontal'
        ? a.position.x - b.position.x
        : a.position.y - b.position.y
    );

  if (selectedNodes.length < 3) return nodes;

  // Calculate positions based on center points for even distribution
  const first = selectedNodes[0];
  const last = selectedNodes[selectedNodes.length - 1];
  const firstDims = getNodeDimensions(first.type);
  const lastDims = getNodeDimensions(last.type);
  
  // Calculate the distance between centers
  const firstCenter = direction === 'horizontal' 
    ? first.position.x + firstDims.width / 2
    : first.position.y + firstDims.height / 2;
    
  const lastCenter = direction === 'horizontal'
    ? last.position.x + lastDims.width / 2
    : last.position.y + lastDims.height / 2;
    
  const totalDistance = lastCenter - firstCenter;
  const spacing = totalDistance / (selectedNodes.length - 1);

  return nodes.map((node) => {
    const index = selectedNodes.findIndex((n) => n.id === node.id);
    if (index === -1 || index === 0 || index === selectedNodes.length - 1) {
      return node;
    }

    const nodeDims = getNodeDimensions(node.type);
    const newPosition = { ...node.position };
    const targetCenter = firstCenter + spacing * index;
    
    if (direction === 'horizontal') {
      newPosition.x = targetCenter - nodeDims.width / 2;
    } else {
      newPosition.y = targetCenter - nodeDims.height / 2;
    }

    return {
      ...node,
      position: snapToGrid(newPosition),
    };
  });
};

/**
 * Create perfectly straight edges using orthogonal routing
 * This ensures arrows are always aligned on the first try
 */
export const createStraightEdges = (edges: Edge[]): Edge[] => {
  return edges.map(edge => ({
    ...edge,
    type: 'straight', // Use React Flow's built-in straight edge type
    // Remove any custom routing that might cause misalignment
    data: {
      ...edge.data,
      // Ensure consistent styling
      strokeWidth: 2,
      stroke: '#3b82f6',
    }
  }));
};

/**
 * Optimize edge routing for better visual alignment
 */
export const optimizeEdgeRouting = (nodes: Node[], edges: Edge[]): Edge[] => {
  return edges.map(edge => {
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);
    
    if (!sourceNode || !targetNode) return edge;
    
    // Calculate optimal handle positions based on node positions
    const sourceCenter = {
      x: sourceNode.position.x + (sourceNode.type === 'add' ? 24 : 144),
      y: sourceNode.position.y + (sourceNode.type === 'add' ? 24 : 64)
    };
    
    const targetCenter = {
      x: targetNode.position.x + (targetNode.type === 'add' ? 24 : 144),
      y: targetNode.position.y + (targetNode.type === 'add' ? 24 : 64)
    };
    
    // Determine optimal handle positions
    let sourceHandle = 'bottom';
    let targetHandle = 'top';
    
    const dx = targetCenter.x - sourceCenter.x;
    const dy = targetCenter.y - sourceCenter.y;
    
    if (Math.abs(dx) > Math.abs(dy)) {
      // Horizontal connection
      if (dx > 0) {
        sourceHandle = 'right';
        targetHandle = 'left';
      } else {
        sourceHandle = 'left';
        targetHandle = 'right';
      }
    } else {
      // Vertical connection
      if (dy > 0) {
        sourceHandle = 'bottom';
        targetHandle = 'top';
      } else {
        sourceHandle = 'top';
        targetHandle = 'bottom';
      }
    }
    
    return {
      ...edge,
      sourceHandle,
      targetHandle,
      // Use straight edges for perfect alignment
      type: 'straight',
      // Ensure consistent marker styling
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
        color: '#3b82f6',
      }
    };
  });
};